/**
 * @file 各种问题的容器，提供自动获取用户回答
 */

import './surveySectionList.scss';

import React, { Component, memo } from 'react';

import { SurveySectionItem } from '../@surveySectionItem/surveySectionItem';

import {
    ESectionValid,
    ISurveySectionInfo,
    IServerUserResultMap,
    ISectionUserResult,
    ISectionResultMap,
    ISectionResult,
    ELEMENT_TYPE as ElementType,
} from '@umu/umu-types';

import {
    buildSectionResult,
    combineSectionListWithUserValue,
    getErrorTip,
    getResultListState,
    getSubmitResult,
    transformSectionList,
} from '../../../../services/@pollService/pollService';
import { components } from 'common';
const { Loading } = components;

interface IProps {
    className?: string;
    style?: React.CSSProperties;
    elementId?: string;
    showInput?: boolean; // 兼容数值型问题
    isShowDescImg?: boolean; // 是否要展示描述图片
    isShowDimensionsLabel?: boolean; // 是否展示维度标签
    elementType?: ElementType;
    sectionList: ISurveySectionInfo[]; // 问题列表
    isDisabled?: boolean;
    needLoadUserResult: boolean; // 是否需要获取用户回答
    updateUserResultKey?: string | number; // 通过比较，强制重新获取 userResult
    getUserResult?: (ids: number[]) => Promise<IServerUserResultMap>; // 获取用户回答的方法
    onDataChange?(result: ISectionUserResult[], invalidMsg: string, sectionId?: number): void;
    reportEmptyDataChange?: boolean;
}

interface IState {
    sectionList: ISurveySectionInfo[];
    transformedSectionList: ISurveySectionInfo[];
    listWithUserValue: ISurveySectionInfo[]; // 拼装了已选回答的列表
    sectionResultMap?: ISectionResultMap;
    loading: boolean;
    userResult: IServerUserResultMap;
    updateUserResultKey?: string | number;
}

export default class SurveySectionList extends Component<IProps, IState> {
    public static getDerivedStateFromProps(nextProps: IProps, _prevState: IState) {
        const sectionList = transformSectionList(nextProps.sectionList);
        const transformedSectionList = transformSectionList(nextProps.sectionList);
        const listWithUserValue = combineSectionListWithUserValue(transformedSectionList, _prevState.userResult);
        return {
            sectionList,
            listWithUserValue,
            transformedSectionList,
            updateUserResultKey: nextProps.updateUserResultKey,
        };
    }

    public sectionResultMap?: ISectionResultMap;

    constructor(props: IProps, state: IState) {
        super(props, state);

        const sectionList = props.sectionList;
        const transformedSectionList = transformSectionList(sectionList);
        this.state = {
            updateUserResultKey: props.updateUserResultKey,
            sectionList,
            transformedSectionList,
            listWithUserValue: transformedSectionList, // 默认假设没有需要恢复数据的情况
            loading: false,
            userResult: {},
        };

        this.initSectionResultMap();
    }

    public shouldComponentUpdate(_nextProps: Readonly<IProps>, nextState: Readonly<IState>, _nextContext: any): boolean {
        if (
            JSON.stringify(nextState.listWithUserValue) !==
            JSON.stringify(this.state.listWithUserValue)
        ) {
            return true;
        }
        if(JSON.stringify(_nextProps) !== JSON.stringify(this.props)) {
            return true;
        }
        return false;
    }

    public initSectionResultMap = () => {
        if (this.state.listWithUserValue.length) {
            this.sectionResultMap = getResultListState(this.state.listWithUserValue);
            this.onValueUpdate();
        }
    };

    public componentDidMount() {
        // props.needLoadUserResult 值为false，此处不需要获取用户回答
        if (this.props.needLoadUserResult) {
            this.loadUserResult();
        }
    }

    public componentDidUpdate(_prevProps: IProps, prevState: IState) {
        // props.needLoadUserResult 值为false，此处不需要获取用户回答
        if (this.props.needLoadUserResult) {
            if (
                JSON.stringify(this.state.transformedSectionList) !==
                JSON.stringify(prevState.transformedSectionList) ||
                this.state.updateUserResultKey !== prevState.updateUserResultKey
            ) {
                this.loadUserResult();
            }
        }
    }

    /**
     * 获取用户回答
     */
    public loadUserResult = async () => {
        const { getUserResult } = this.props;
        if (typeof getUserResult !== 'function') {
            return;
        }
        const transformedSectionList = this.state.transformedSectionList;
        if (this.state.loading || transformedSectionList.length === 0) {
            return;
        }

        this.setState({
            loading: true,
        });

        if (!getUserResult) {
            return;
        }

        const ids = transformedSectionList.map((v) => {
            return v.id;
        });

        // async 获取 userResult，方便在外面把从服务端获取的数据转换成 IServerUserResultMap 格式
        const userResult: IServerUserResultMap = await getUserResult(ids);
        this.setState(
            {
                loading: false,
                userResult,
                listWithUserValue: combineSectionListWithUserValue(transformedSectionList, userResult),
            },
            () => {
                this.initSectionResultMap();
            },
        );
    };

    /**
     * 更新每个题目的中间结果
     * 除了题目组件中传递的 result 和 valid 之外，再通过 buildSectionResult 补充当前题目的信息
     */
    public onValueUpdate = (
        sectionInfo?: ISurveySectionInfo,
        sectionResult?: ISectionResult,
        isValid?: ESectionValid,
    ) => {
        let sectionResultMap = Object.assign({}, this.sectionResultMap);
        let sectionId = 0;
        if (sectionInfo) {
            sectionId = sectionInfo.id;
            sectionResultMap = Object.assign(sectionResultMap, {
                [sectionId]: buildSectionResult(sectionInfo, sectionResult, isValid),
            });
            this.sectionResultMap = sectionResultMap;
        }

        const { onDataChange } = this.props;
        if (onDataChange) {
            // 根据中间结果获取提交用的数据结构和错误信息
            const errorTip = getErrorTip(sectionResultMap);
            const submitResult = getSubmitResult(sectionResultMap, this.props.reportEmptyDataChange);
            onDataChange(submitResult, errorTip, sectionId);
        }
    }

    public mergeUserResultFromSectionResultMap(listWithUserValue: ISurveySectionInfo[]) {
        // 如果本地有用户回答，就用本地的用户回答覆盖掉服务端的用户回答
        const userResult: ISurveySectionInfo[] = [];
        listWithUserValue.forEach((sectionInfo, index) => {
            const { id } = sectionInfo;
            const sectionResult = this.sectionResultMap && this.sectionResultMap[id];
            if (sectionResult && sectionResult.userValue) {
                userResult[index] = {
                    ...sectionInfo,
                    userValue: sectionResult.userValue.length ? sectionResult.userValue : sectionInfo.userValue,
                };
            } else {
                userResult[index] = sectionInfo;
            }

        });
        return userResult;
    }

    public renderSections() {
        const {
            isDisabled,
            elementId,
            elementType,
            showInput,
            isShowDimensionsLabel = false,
            isShowDescImg = false,
        } = this.props;
        const { listWithUserValue } = this.state;
        const renderList = this.mergeUserResultFromSectionResultMap(listWithUserValue);
        return (
            <div className={`section-list ${this.props.className || ''}` } style={this.props.style || {}}>
                {renderList.map((sectionInfo: ISurveySectionInfo) => {
                    // 确定数值型问题选择分数的单位，默认为1
                    const step =
                        sectionInfo.extend.setup &&
                            sectionInfo.extend.setup.dot_num &&
                            sectionInfo.extend.setup.dot_num === 1
                            ? 0.1
                            : 1;
                    return (
                        <SurveySectionItem
                            step={step}
                            showInput={showInput}
                            key={sectionInfo.id}
                            isShowDescImg={isShowDescImg}
                            elementId={elementId}
                            elementType={elementType}
                            sectionInfo={sectionInfo}
                            isDisabled={isDisabled}
                            isShowDimensionsLabel={isShowDimensionsLabel}
                            onValueUpdate={(value: ISectionResult, isValid: ESectionValid) => {
                                this.onValueUpdate(sectionInfo, value, isValid);
                            }}
                        ></SurveySectionItem>
                    );
                })}
            </div>
        );
    }

    public render() {
        if (this.state.loading && !this.state.listWithUserValue.length) {
            return <Loading />;
        }
        const Sections = memo(this.renderSections.bind(this));
        return <Sections />;
    }
}
